import { useQueryClient } from '@tanstack/react-query';
import { useTransition } from 'react';
import { useTranslations } from '@/i18n/translations';
import { Button, type ButtonSize, type ButtonVariant } from '@leanin/ui';

type Props = {
  label?: string;
  size?: ButtonSize;
  variant?: ButtonVariant;
  /** Reloads only what failed, for a state embedded in a page that loaded
   *  fine (a chat window, a pane). */
  onRetry?: () => Promise<unknown>;
};

/**
 * Recovery CTA for error states. By default it invalidates the entire React
 * Query cache, so every active query refetches and the page reconciles with
 * the server. In a Vite SPA there is no `router.refresh()`; query
 * invalidation is the equivalent — every `useQuery` whose `enabled` is true
 * will refetch. The button shows as loading until the reload settles.
 */
export const RetryButton = ({
  label,
  size = 'md',
  variant = 'primary',
  onRetry,
}: Props) => {
  const t = useTranslations('Shell');
  const qc = useQueryClient();
  const [pending, start] = useTransition();
  return (
    <Button
      size={size}
      variant={variant}
      loading={pending}
      onClick={() =>
        start(async () => {
          await (onRetry ? onRetry() : qc.invalidateQueries());
        })
      }
    >
      {label ?? t('retry')}
    </Button>
  );
};
